import type { GuideChapterType } from './GuideApi';

/**
 * A single piece of a snippet. `matched` segments are the parts that literally match the query and
 * are meant to be highlighted (e.g. wrapped in <mark>) by the UI. Splitting the snippet into
 * segments lets the caller render the highlight WITHOUT injecting raw HTML from the documentation.
 */
export interface GuideSearchSegment {
    text: string;
    matched: boolean;
}

/** One page that matches the search query, with a context snippet built around the first match. */
export interface GuideSearchHit {
    chapterKey: string;
    chapterTitle: string;
    slug: string;
    title: string;
    /** ~50 words of context around the first match, split into highlightable segments. */
    snippet: GuideSearchSegment[];
}

/** How many words of context the snippet keeps around the first match. */
const SNIPPET_WORDS = 50;

/**
 * Turns the raw page HTML into searchable plain text. Uses a throw-away DOM element when a document
 * is available (browser / jsdom) so entities and nested markup collapse correctly, and falls back
 * to a tag-stripping regex otherwise.
 */
export function stripHtml(html: string): string {
    if (typeof document !== 'undefined') {
        const el = document.createElement('div');
        el.innerHTML = html;
        return (el.textContent || '').replace(/\s+/g, ' ').trim();
    }
    return html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Splits a single word into segments, marking every (case-insensitive) occurrence of `query` as
 * matched while preserving the original casing of the surrounding text.
 */
function splitWord(word: string, query: string): GuideSearchSegment[] {
    const segments: GuideSearchSegment[] = [];
    const lower = word.toLowerCase();
    let from = 0;
    let pos = lower.indexOf(query, from);
    while (pos !== -1) {
        if (pos > from) {
            segments.push({ text: word.slice(from, pos), matched: false });
        }
        segments.push({ text: word.slice(pos, pos + query.length), matched: true });
        from = pos + query.length;
        pos = lower.indexOf(query, from);
    }
    if (from < word.length) {
        segments.push({ text: word.slice(from), matched: false });
    }
    return segments;
}

/**
 * Builds the snippet segments: a window of at most SNIPPET_WORDS words centred on the word that
 * contains the first match, with matched substrings flagged. When the query is not found in the
 * text (e.g. it only matched the page title) the first SNIPPET_WORDS words are returned unmarked.
 */
function buildSnippet(text: string, query: string): GuideSearchSegment[] {
    const words = text.split(/\s+/).filter(word => word.length > 0);
    if (words.length === 0) {
        return [];
    }

    let matchIndex = words.findIndex(word => word.toLowerCase().includes(query));
    if (matchIndex === -1) {
        matchIndex = 0;
    }

    let start = Math.max(0, matchIndex - Math.floor(SNIPPET_WORDS / 2));
    let end = Math.min(words.length, start + SNIPPET_WORDS);
    start = Math.max(0, end - SNIPPET_WORDS);

    const window = words.slice(start, end);
    const segments: GuideSearchSegment[] = [];

    if (start > 0) {
        segments.push({ text: '… ', matched: false });
    }
    window.forEach((word, index) => {
        if (index > 0) {
            segments.push({ text: ' ', matched: false });
        }
        segments.push(...splitWord(word, query));
    });
    if (end < words.length) {
        segments.push({ text: ' …', matched: false });
    }

    return segments;
}

/**
 * Searches every page of every chapter for `query`, matching against the page title and the plain
 * text extracted from the page HTML. Matching is case-insensitive. Each hit carries a ~50-word
 * context snippet around the first match, ready to be rendered with highlighting.
 *
 * A blank or whitespace-only query returns no hits.
 */
export function searchGuide(chapters: GuideChapterType[], query: string): GuideSearchHit[] {
    const normalized = query.trim().toLowerCase();
    if (normalized === '') {
        return [];
    }

    return chapters.flatMap(chapter =>
        chapter.pages
            .map(page => {
                const text = stripHtml(page.html);
                const titleMatch = page.title.toLowerCase().includes(normalized);
                const contentMatch = text.toLowerCase().includes(normalized);
                if (!titleMatch && !contentMatch) {
                    return null;
                }
                return {
                    chapterKey: chapter.key,
                    chapterTitle: chapter.title,
                    slug: page.slug,
                    title: page.title,
                    snippet: buildSnippet(text, normalized),
                };
            })
            .filter((hit): hit is GuideSearchHit => hit !== null)
    );
}
